/**
 * Estimation indicative du capital AMG — fonction pure, sans side effect.
 *
 * Modèle « besoins », aligné mot pour mot sur le guide (Partie II — « Faisons votre chiffre ») :
 *   Capital ≈ dettes + (charges mensuelles × mois de répit).
 * Le revenu net qui s'arrêterait sert au raisonnement (combien de mois de ce revenu
 * le capital remplacerait), pas au montant lui-même.
 *
 * Arrondi à 5 000 $. Indicatif seulement — la validation se fait en audit (disclaimer côté UI).
 */

export interface CalcInputs {
  dettes: number; // $ — hypothèque + autres dettes à couvrir
  chargesMensuelles: number; // $/mois — coût du foyer
  moisRepit: number; // nombre de mois de répit souhaités
  revenuNet: number; // $/mois — revenu net qui s'arrêterait
}

export interface CalcBreakdownItem {
  label_fr: string;
  label_en: string;
  amount: number; // $ absolus
}

export interface CalcResult {
  recommended: number; // $ absolus, arrondi à 5 000
  breakdown: CalcBreakdownItem[];
  reasoning_fr: string;
  reasoning_en: string;
}

const CAP = 3_000_000;
const MONTHS_MAX = 60;
const ROUND_STEP = 5_000;

/** Garde-fou : un nombre fini strictement positif, sinon 0. */
function clean(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0;
}

function roundTo(n: number, step: number): number {
  return Math.round(n / step) * step;
}

function fmt(n: number, lang: 'fr' | 'en'): string {
  return n.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA');
}

export function recommendCapital(inputs: CalcInputs): CalcResult {
  const dettes = clean(inputs.dettes);
  const charges = clean(inputs.chargesMensuelles);
  const mois = Math.min(MONTHS_MAX, Math.round(clean(inputs.moisRepit)));
  const revenu = clean(inputs.revenuNet);

  const coutVie = charges * mois;
  const recommended = Math.min(CAP, roundTo(dettes + coutVie, ROUND_STEP));

  const breakdown: CalcBreakdownItem[] = [
    {
      label_fr: 'Régler ou couvrir vos dettes',
      label_en: 'Clear or cover your debts',
      amount: dettes
    },
    {
      label_fr: mois > 0 ? `Faire vivre votre foyer pendant ${mois} mois` : 'Faire vivre votre foyer',
      label_en: mois > 0 ? `Keep your household running for ${mois} months` : 'Keep your household running',
      amount: coutVie
    }
  ];

  return {
    recommended,
    breakdown,
    reasoning_fr: buildReasoning(charges, mois, revenu, recommended, 'fr'),
    reasoning_en: buildReasoning(charges, mois, revenu, recommended, 'en')
  };
}

function buildReasoning(
  charges: number,
  mois: number,
  revenu: number,
  recommended: number,
  lang: 'fr' | 'en'
): string {
  if (recommended <= 0) {
    return lang === 'fr'
      ? 'Entrez vos chiffres pour obtenir une estimation.'
      : 'Enter your numbers to get an estimate.';
  }

  if (lang === 'fr') {
    const base = `Couvrir vos charges de ${fmt(charges, 'fr')} $/mois pendant ${mois} mois, en plus de vos dettes, demande un capital d'environ ${fmt(recommended, 'fr')} $.`;
    const rev =
      revenu > 0
        ? ` De quoi remplacer un revenu net de ${fmt(revenu, 'fr')} $/mois pendant environ ${Math.max(1, Math.round(recommended / revenu))} mois.`
        : '';
    return base + rev;
  }

  const base = `Covering your ${fmt(charges, 'en')} CAD/month expenses for ${mois} months, plus your debts, calls for about ${fmt(recommended, 'en')} CAD.`;
  const rev =
    revenu > 0
      ? ` Enough to replace a net income of ${fmt(revenu, 'en')} CAD/month for roughly ${Math.max(1, Math.round(recommended / revenu))} months.`
      : '';
  return base + rev;
}
